import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Avatar, Button, Chip } from '@/ui/primitives';
import { formatRelative } from '@/lib/format';
import { ROLE_LABEL, type User } from '../schemas';
import { useAreas } from '../api';

interface Props {
  users: User[];
  search: string;
  roleFilter: string;
  statusFilter: string;
  onEdit: (user: User) => void;
  onDelete: (user: User) => void;
  onResetPassword: (user: User) => void;
}

export function UsersTable({
  users,
  search,
  roleFilter,
  statusFilter,
  onEdit,
  onDelete,
  onResetPassword,
}: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'name', desc: false }]);
  // Catálogo completo — admin vê todas as áreas como etiquetas implícitas.
  const { data: allAreas = [] } = useAreas();

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !u.name.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q))
        return false;
      if (roleFilter && u.role !== roleFilter) return false;
      if (statusFilter === 'ativo' && !u.active) return false;
      if (statusFilter === 'inativo' && u.active) return false;
      return true;
    });
  }, [users, search, roleFilter, statusFilter]);

  const columns = useMemo<ColumnDef<User>[]>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: 'Nome',
        cell: ({ row }) => {
          const u = row.original;
          return (
            <div className="flex items-center gap-2.5 min-w-0">
              <Avatar name={u.name} color={u.avatar_color ?? '#e96a45'} size="sm" />
              <div className="flex flex-col min-w-0">
                <span className="text-sm text-text truncate">{u.name}</span>
                <span className="text-xs text-text-muted truncate">{u.email}</span>
              </div>
            </div>
          );
        },
      },
      {
        id: 'cargo',
        accessorKey: 'cargo',
        header: 'Cargo',
        cell: ({ getValue }) => (
          <span className="text-sm text-text-2">{(getValue() as string | null) || '—'}</span>
        ),
      },
      {
        id: 'areas',
        accessorKey: 'areas',
        header: 'Áreas',
        enableSorting: false,
        cell: ({ row, getValue }) => {
          // Admin recebe todas as áreas do catálogo como etiquetas implícitas
          // (na prática enxerga todos os módulos), em vez do que está em
          // user_area. Outros roles seguem o vínculo real.
          const isAdmin = (row.original as User).role === 'admin';
          const userAreas =
            (getValue() as Array<{ id: number; nome: string; cor?: string | null }>) ?? [];
          const areas = isAdmin
            ? allAreas.map((a) => ({ id: Number(a.id), nome: a.nome, cor: a.cor ?? null }))
            : userAreas;
          if (!areas.length) return <span className="text-sm text-text-muted">—</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {areas.map((a) => (
                <span
                  key={a.id}
                  className="inline-flex items-center text-[0.7rem] font-medium px-2 py-0.5 rounded-full"
                  style={{
                    background: a.cor ? `${a.cor}1f` : 'var(--surface-2)',
                    color: a.cor ?? 'var(--text-2)',
                  }}
                >
                  {a.nome}
                </span>
              ))}
            </div>
          );
        },
      },
      {
        id: 'role',
        accessorKey: 'role',
        header: 'Papel',
        cell: ({ getValue }) => {
          const r = getValue() as string;
          const tone = r === 'admin' ? 'accent' : r === 'gestor' ? 'info' : 'neutral';
          return (
            <Chip tone={tone} size="sm">
              {ROLE_LABEL[r as keyof typeof ROLE_LABEL] ?? r}
            </Chip>
          );
        },
      },
      {
        id: 'active',
        accessorKey: 'active',
        header: 'Status',
        cell: ({ getValue }) => {
          const active = getValue() as boolean;
          return (
            <Chip tone={active ? 'ok' : 'danger'} size="sm" dot>
              {active ? 'Ativo' : 'Inativo'}
            </Chip>
          );
        },
      },
      {
        id: 'last_login',
        accessorKey: 'last_login',
        header: 'Último acesso',
        cell: ({ getValue }) => {
          const v = getValue() as string | null;
          return (
            <span className="text-xs text-text-muted">{v ? formatRelative(v) : 'Nunca'}</span>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1">
            <Button size="sm" variant="ghost" onClick={() => onEdit(row.original)}>
              Editar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onResetPassword(row.original)}
            >
              Resetar senha
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(row.original)}>
              {row.original.active ? 'Desativar' : 'Reativar'}
            </Button>
          </div>
        ),
      },
    ],
    [onDelete, onEdit, onResetPassword, allAreas],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <table className="ds-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  onClick={h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined}
                  className={h.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                  {h.column.getIsSorted() === 'asc' && ' ↑'}
                  {h.column.getIsSorted() === 'desc' && ' ↓'}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="p-6 text-center text-sm text-text-muted">
          Nenhum usuário encontrado.
        </div>
      )}
    </div>
  );
}
